import { Box, Container, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { keyframes } from "@mui/system";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const slideRight = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const servicesData = [
  {
    icon: "🤖",
    number: "01",
    title: "AI & Agentic AI Solutions",
    description: "Intelligent automation and AI-powered agents to transform your business processes and unlock new capabilities.",
    accent: "#5b7cfa",
    light: "rgba(91,124,250,0.08)",
    url: "/services/ai-agentic-solutions",
  },
  {
    icon: "💡",
    number: "02",
    title: "IT Consulting & Strategy",
    description: "Strategic IT guidance and consulting to align technology with your business goals for sustainable growth.",
    accent: "#764ba2",
    light: "rgba(118,75,162,0.08)",
    url: "/services/it-consulting-strategy",
  },
  {
    icon: "⚙️",
    number: "03",
    title: "Custom App Development",
    description: "Tailored software solutions built from scratch to meet your unique business requirements and scale with you.",
    accent: "#3b82f6",
    light: "rgba(59,130,246,0.08)",
    url: "/services/custom-app-development",
  },
  {
    icon: "✅",
    number: "04",
    title: "Quality Engineering & QA",
    description: "Comprehensive testing and quality assurance for reliable, high-performance software delivery every release.",
    accent: "#8b5cf6",
    light: "rgba(139,92,246,0.08)",
    url: "/services/quality-engineering-assurance",
  },
  {
    icon: "📱",
    number: "05",
    title: "Mobile App Development",
    description: "Cross-platform mobile applications with exceptional UX that engage users and drive business outcomes.",
    accent: "#06b6d4",
    light: "rgba(6,182,212,0.08)",
    url: "/services/mobile-app-development",
  },
  {
    icon: "🔧",
    number: "06",
    title: "Managed IT Services",
    description: "Complete IT infrastructure management, proactive monitoring, and 24/7 technical support you can count on.",
    accent: "#a855f7",
    light: "rgba(168,85,247,0.08)",
    url: "/services/managed-it-services",
  },
];

const ServicesSection = () => {
  return (
    <Box
      sx={{
        width: "100%",
        py: { xs: 8, sm: 10, md: 14 },
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Large faint gradient blob — top left */}
      <Box
        sx={{
          position: "absolute",
          top: "-200px",
          left: "-200px",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(91,124,250,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Large faint gradient blob — bottom right */}
      <Box
        sx={{
          position: "absolute",
          bottom: "-200px",
          right: "-200px",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(118,75,162,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section Heading */}
        <Box sx={{ textAlign: "center", mb: { xs: 6, sm: 8, md: 10 } }}>
          {/* Pill label */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.5,
              py: 0.8,
              borderRadius: "999px",
              border: "1px solid rgba(91,124,250,0.3)",
              background: "rgba(91,124,250,0.06)",
              mb: 3,
              animation: `${fadeUp} 0.6s ease-out both`,
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #5b7cfa, #764ba2)",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  background: "#5b7cfa",
                  animation: `${pulseRing} 1.8s ease-out infinite`,
                },
              }}
            />
            <Typography sx={{ color: "#5b7cfa", fontSize: 12, fontWeight: 700, letterSpacing: 1.5 }}>
              WHAT WE OFFER
            </Typography>
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "30px", sm: "40px", md: "52px" },
              fontWeight: 800,
              letterSpacing: "-1px",
              lineHeight: 1.15,
              mb: 2.5,
              animation: `${fadeUp} 0.7s ease-out 0.1s both`,
              background: "linear-gradient(135deg, #1a1a2e 0%, #5b7cfa 50%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Our Services
          </Typography>

          {/* Animated underline */}
          <Box
            sx={{
              width: 80,
              height: 4,
              borderRadius: 2,
              background: "linear-gradient(90deg, #5b7cfa, #764ba2)",
              mx: "auto",
              mb: 3,
              transformOrigin: "left",
              animation: `${slideRight} 0.8s ease-out 0.3s both`,
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px", md: "19px" },
              color: "#64748b",
              maxWidth: 600,
              mx: "auto",
              lineHeight: 1.8,
              animation: `${fadeUp} 0.7s ease-out 0.2s both`,
            }}
          >
            Comprehensive solutions designed to elevate your business and drive measurable success
          </Typography>
        </Box>

        {/* Services Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
            gap: { xs: 3, sm: 4 },
          }}
        >
          {servicesData.map((service, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                p: { xs: "28px", sm: "32px" },
                background: "#fff",
                borderRadius: "20px",
                border: "1px solid #e8edf5",
                boxShadow: "0 2px 16px rgba(0,0,0,0.04)",
                transition: "all 0.35s ease",
                animation: `${fadeUp} 0.6s ease-out ${index * 0.07}s both`,
                overflow: "hidden",
                cursor: "default",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: `0 20px 48px ${service.accent}22`,
                  border: `1px solid ${service.accent}44`,
                },
                "&:hover .svc-bar": {
                  transform: "scaleX(1)",
                },
                "&:hover .svc-icon-box": {
                  background: `linear-gradient(135deg, ${service.accent}30, ${service.accent}55)`,
                  transform: "scale(1.08) rotate(-4deg)",
                },
                "&:hover .svc-title": {
                  color: service.accent,
                },
              }}
            >
              {/* Animated left accent bar */}
              <Box
                className="svc-bar"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 4,
                  height: "100%",
                  background: `linear-gradient(180deg, ${service.accent}, ${service.accent}44)`,
                  borderRadius: "20px 0 0 20px",
                  transform: "scaleX(0)",
                  transformOrigin: "top",
                  transition: "transform 0.35s ease",
                }}
              />

              {/* Number — faint background */}
              <Typography
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 18,
                  fontSize: "52px",
                  fontWeight: 900,
                  color: `${service.accent}14`,
                  lineHeight: 1,
                  userSelect: "none",
                  fontFamily: "monospace",
                  letterSpacing: "-2px",
                }}
              >
                {service.number}
              </Typography>

              {/* Icon */}
              <Box
                className="svc-icon-box"
                sx={{
                  width: { xs: 56, sm: 64 },
                  height: { xs: 56, sm: 64 },
                  borderRadius: "16px",
                  background: service.light,
                  border: `1px solid ${service.accent}22`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: { xs: "26px", sm: "30px" },
                  mb: 2.5,
                  transition: "all 0.35s ease",
                }}
              >
                {service.icon}
              </Box>

              <Typography
                className="svc-title"
                variant="h6"
                sx={{
                  fontSize: { xs: "18px", sm: "20px" },
                  fontWeight: 700,
                  mb: 1.5,
                  color: "#1a1a2e",
                  transition: "color 0.3s ease",
                  lineHeight: 1.3,
                }}
              >
                {service.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "16px" },
                  color: "#64748b",
                  lineHeight: 1.75,
                }}
              >
                {service.description}
              </Typography>

              {/* Read More link */}
              <Box
                component={Link}
                to={service.url}
                sx={{
                  mt: 3,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  color: service.accent,
                  fontSize: "14px",
                  fontWeight: 700,
                  textDecoration: "none",
                  transition: "gap 0.25s ease",
                  "&:hover": {
                    gap: 1.2,
                  },
                }}
              >
                Read More
                <Box component="span" sx={{ fontSize: "16px", lineHeight: 1 }}>→</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;
